import { isValidCoordinates, sanitizeDisplayText } from "lib/safeDisplayText";

const FETCH_TIMEOUT_MS = 10_000;
const WEATHER_CODE_MAX = 99;

/** GET JSON with a hard timeout; used only in the browser. */
async function fetchJsonWithTimeout<T>(url: string): Promise<T | null> {
  if (typeof window === "undefined") return null;
  const ctrl = new AbortController();
  const timer = window.setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      signal: ctrl.signal,
      credentials: "omit",
      mode: "cors",
      cache: "no-store",
    });
    if (!res.ok) return null;
    const ct = res.headers.get("content-type");
    if (ct && !ct.includes("json")) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  } finally {
    window.clearTimeout(timer);
  }
}

/** City / region name for map coordinates (language from locale). */
export async function reverseGeocodeCity(
  lat: number,
  lng: number,
  localityLanguage: string
): Promise<string | null> {
  if (!isValidCoordinates(lat, lng)) return null;
  const latR = Math.round(lat * 1e6) / 1e6;
  const lngR = Math.round(lng * 1e6) / 1e6;
  const raw = localityLanguage.trim().toLowerCase().slice(0, 8);
  const lang = /^[a-z]{2}$/.test(raw) ? raw : "en";
  const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${encodeURIComponent(
    String(latR)
  )}&longitude=${encodeURIComponent(String(lngR))}&localityLanguage=${encodeURIComponent(lang)}`;
  const data = await fetchJsonWithTimeout<{
    city?: unknown;
    locality?: unknown;
    principalSubdivision?: unknown;
  }>(url);
  if (!data) return null;
  const pick = [data.city, data.locality, data.principalSubdivision].find(
    (v) => typeof v === "string" && v.length > 0
  );
  const sanitized = sanitizeDisplayText(pick, 120);
  return sanitized.length > 0 ? sanitized : null;
}

/** WMO weather code from Open-Meteo “current weather”. */
export async function fetchWeatherCode(lat: number, lng: number): Promise<number | null> {
  if (!isValidCoordinates(lat, lng)) return null;
  const latR = Math.round(lat * 1e4) / 1e4;
  const lngR = Math.round(lng * 1e4) / 1e4;
  const url = `https://api.open-meteo.com/v1/forecast?latitude=${encodeURIComponent(
    String(latR)
  )}&longitude=${encodeURIComponent(String(lngR))}&current_weather=true`;
  const data = await fetchJsonWithTimeout<{ current_weather?: { weathercode?: unknown } }>(url);
  const code = data?.current_weather?.weathercode;
  if (typeof code !== "number" || !Number.isFinite(code) || code < 0 || code > WEATHER_CODE_MAX) {
    return null;
  }
  return Math.round(code);
}
